import { Link, useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import { SCENARIOS } from '../data/scenarios';
import './Narrative.css';

/**
 * One scenario page — /scenarios/:id
 *
 * An unknown id (or a retired one) falls back to a short page that points back
 * to the hub, rather than rendering an empty shell.
 */
function ScenarioDetail() {
  const { id } = useParams();
  const index = SCENARIOS.findIndex((s) => s.id === id);
  const scenario = index >= 0 ? SCENARIOS[index] : null;

  if (!scenario) {
    return (
      <div className="np-page">
        <SEO
          title="Scenario not found | Codentic AI"
          description="That scenario does not exist or has been retired."
          robots="noindex, follow"
        />
        <section className="np-hero np-hero--compact">
          <div className="container">
            <span className="np-eyebrow">Scenarios</span>
            <h1>That scenario is not here</h1>
            <p className="np-hero-sub">
              The link may be old or mistyped. The full list is on the Scenarios page.
            </p>
            <div className="np-cta-row np-cta-row--center">
              <Link to="/scenarios" className="np-btn np-btn--primary">All scenarios</Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const prev = index > 0 ? SCENARIOS[index - 1] : null;
  const next = index < SCENARIOS.length - 1 ? SCENARIOS[index + 1] : null;

  return (
    <div className="np-page">
      <SEO
        title={`${scenario.name} — EWM Scenario | Codentic AI`}
        description={`${scenario.tagline} ${scenario.approach}`.slice(0, 300)}
        url={`https://www.codenticai.com/scenarios/${scenario.id}`}
      />

      {/* Hero */}
      <section className="np-hero">
        <div className="container">
          <span className="np-eyebrow">
            <span aria-hidden="true">{scenario.icon}</span> Scenario
          </span>
          <h1>{scenario.name}</h1>
          <p className="np-hero-sub">{scenario.tagline}</p>
        </div>
      </section>

      {/* Hosts */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">What the side-car sat beside</h2>
          <ul className="np-list">
            {scenario.hosts.map((h) => (
              <li key={h}>{h}</li>
            ))}
          </ul>

          <h3 className="np-h3" style={{ marginTop: '34px' }}>The constraint</h3>
          <p className="np-body">{scenario.problem}</p>

          <h3 className="np-h3" style={{ marginTop: '26px' }}>What the side-car does about it</h3>
          <p className="np-body">{scenario.approach}</p>

          <div className="np-badges" style={{ marginTop: '26px' }}>
            {scenario.framework.map((f) => (
              <span key={f} className="np-badge">{f}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Measured */}
      <section className="np-section np-section--alt">
        <div className="container">
          <h2 className="np-h2">What the run measured</h2>
          <p className="np-lead">
            Figures from the run itself. Where a number has a limit, the limit is written below it
            rather than left out.
          </p>
          <div className="np-stats">
            {scenario.measured.map((m) => (
              <div key={m.label} className="np-stat">
                <span className="np-stat-value">{m.value}</span>
                <span className="np-stat-label">{m.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Evidence */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">What the runs actually show</h2>
          <ul className="np-list">
            {scenario.evidence.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>

          {scenario.caveats.length > 0 && (
            <div className="np-caveat">
              <h3 className="np-h3">Where this is weaker than it looks</h3>
              <ul className="np-list">
                {scenario.caveats.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="np-source" style={{ marginTop: '26px' }}>
            Source notebook{scenario.notebooks.length > 1 ? 's' : ''}:{' '}
            {scenario.notebooks.join(', ')} — from the ewm-state-machine repository.
          </div>
        </div>
      </section>

      {/* Prev / next */}
      <section className="np-section np-section--alt">
        <div className="container np-nav-row">
          {prev ? (
            <Link to={`/scenarios/${prev.id}`} className="np-nav-link">
              <span className="np-nav-dir">Previous scenario</span>
              <span className="np-nav-name">
                <span aria-hidden="true">{prev.icon}</span> {prev.name}
              </span>
            </Link>
          ) : (
            <span />
          )}
          {next ? (
            <Link to={`/scenarios/${next.id}`} className="np-nav-link np-nav-link--right">
              <span className="np-nav-dir">Next scenario</span>
              <span className="np-nav-name">
                <span aria-hidden="true">{next.icon}</span> {next.name}
              </span>
            </Link>
          ) : (
            <span />
          )}
        </div>
        <div className="container">
          <div className="np-cta-row np-cta-row--center">
            <Link to="/scenarios" className="np-btn np-btn--secondary">All scenarios</Link>
            <Link to="/applications" className="np-btn np-btn--secondary">Applications by industry</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ScenarioDetail;
